import { useRef, useState, useCallback, useEffect, RefObject } from "react";
import {
  ANIM_DURATION_MS,
  MOBILE_BREAKPOINT,
  ENABLE_EXPANSION,
  PHONE_VIEWPORT_BREAKPOINT,
  BASE_CARD_WIDTH,
  BASE_CARD_HEIGHT,
  EXPANDED_WIDTH_MULTIPLIER,
  EXPANDED_HEIGHT_MULTIPLIER,
  FLIP_TRANSITION_DURATION,
} from "./carouselShared";

// ============================================================================
// useCarouselAnimation
// ============================================================================

interface UseCarouselAnimationReturn {
  scrollOffset: number;
  isAnimating: boolean;
  animateTo: (
    targetOffset: number,
    durationMs?: number,
    onComplete?: () => void,
  ) => void;
}

export const useCarouselAnimation = (): UseCarouselAnimationReturn => {
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | null>(null);
  const animStartOffsetRef = useRef<number>(0);
  const animDeltaRef = useRef<number>(0);
  const animStartTimeRef = useRef<number>(0);

  const animateTo = useCallback(
    (
      targetOffset: number,
      durationMs: number = ANIM_DURATION_MS,
      onComplete?: () => void,
    ) => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setIsAnimating(true);
      setScrollOffset((currentOffset) => {
        const start = currentOffset;
        const delta = targetOffset - start;
        animStartOffsetRef.current = start;
        animDeltaRef.current = delta;
        animStartTimeRef.current = performance.now();

        const easeInOutCubic = (t: number) =>
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        const tick = () => {
          const now = performance.now();
          const elapsed = now - animStartTimeRef.current;
          const t = Math.min(1, elapsed / durationMs);
          const eased = easeInOutCubic(t);
          const next =
            animStartOffsetRef.current + animDeltaRef.current * eased;
          setScrollOffset(next);
          if (t < 1) {
            animationRef.current = requestAnimationFrame(tick);
          } else {
            animationRef.current = null;
            setScrollOffset(targetOffset);
            setIsAnimating(false);
            if (onComplete) onComplete();
          }
        };

        animationRef.current = requestAnimationFrame(tick);
        return currentOffset;
      });
    },
    [],
  );

  return { scrollOffset, isAnimating, animateTo };
};

// ============================================================================
// useContainerSize
// ============================================================================

interface ContainerSize {
  width: number;
  height: number;
}

export const useContainerSize = (
  trackRef: RefObject<HTMLDivElement>,
): ContainerSize => {
  const [containerSize, setContainerSize] = useState<ContainerSize>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [trackRef]);

  return containerSize;
};

// ============================================================================
// useMobileDetection
// ============================================================================

export const useMobileDetection = (): boolean => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Use matchMedia API for better performance
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`,
    );

    const updateMobile = (e: MediaQueryList | MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Use addEventListener if available (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateMobile);
      return () => mediaQuery.removeEventListener("change", updateMobile);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(updateMobile);
      return () => mediaQuery.removeListener(updateMobile);
    }
  }, []);

  return isMobile;
};

// ============================================================================
// useCardExpansion
// ============================================================================

interface UseCardExpansionReturn {
  expandedIndex: number | null;
  overlayShown: boolean;
  expandedCardRef: React.RefObject<HTMLDivElement>;
  firstRectRef: React.MutableRefObject<DOMRect | null>;
  lastRectRef: React.MutableRefObject<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>;
  handleExpand: (index: number, elementRef: HTMLDivElement | null) => void;
  handleClose: () => void;
}

export const useCardExpansion = (): UseCardExpansionReturn => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [overlayShown, setOverlayShown] = useState(false);
  const expandedCardRef = useRef<HTMLDivElement | null>(null);
  const firstRectRef = useRef<DOMRect | null>(null);
  const lastRectRef = useRef<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const transitionEndNodeRef = useRef<HTMLDivElement | null>(null);
  const transitionEndHandlerRef = useRef<(() => void) | null>(null);

  const handleExpand = useCallback(
    (index: number, elementRef: HTMLDivElement | null) => {
      if (!ENABLE_EXPANSION) return;

      // Prevent expansion on phone-sized viewports
      const isPhoneViewport = window.matchMedia(
        `(max-width: ${PHONE_VIEWPORT_BREAKPOINT}px)`,
      ).matches;
      if (isPhoneViewport) return;

      if (!elementRef) return;

      const first = elementRef.getBoundingClientRect();
      firstRectRef.current = first;

      // Calculate target (centered) rect at 200% x 120%
      const targetWidth = BASE_CARD_WIDTH * EXPANDED_WIDTH_MULTIPLIER;
      const targetHeight = BASE_CARD_HEIGHT * EXPANDED_HEIGHT_MULTIPLIER;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const targetLeft = cx - targetWidth / 2;
      const targetTop = cy - targetHeight / 2;
      lastRectRef.current = {
        left: targetLeft,
        top: targetTop,
        width: targetWidth,
        height: targetHeight,
      };

      setExpandedIndex(index);
      setOverlayShown(false);
      requestAnimationFrame(() => {
        if (
          !expandedCardRef.current ||
          !firstRectRef.current ||
          !lastRectRef.current
        )
          return;
        // Initialize to FIRST rect exactly
        expandedCardRef.current.style.transition = "none";
        expandedCardRef.current.style.left = `${firstRectRef.current.left}px`;
        expandedCardRef.current.style.top = `${firstRectRef.current.top}px`;
        expandedCardRef.current.style.width = `${firstRectRef.current.width}px`;
        expandedCardRef.current.style.height = `${firstRectRef.current.height}px`;
        // Next frame, animate to LAST rect (corners move precisely)
        requestAnimationFrame(() => {
          if (!expandedCardRef.current || !lastRectRef.current) return;
          setOverlayShown(true);
          expandedCardRef.current.style.transition = `left ${FLIP_TRANSITION_DURATION} ease, top ${FLIP_TRANSITION_DURATION} ease, width ${FLIP_TRANSITION_DURATION} ease, height ${FLIP_TRANSITION_DURATION} ease`;
          expandedCardRef.current.style.left = `${lastRectRef.current.left}px`;
          expandedCardRef.current.style.top = `${lastRectRef.current.top}px`;
          expandedCardRef.current.style.width = `${lastRectRef.current.width}px`;
          expandedCardRef.current.style.height = `${lastRectRef.current.height}px`;
        });
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    if (
      !expandedCardRef.current ||
      !firstRectRef.current ||
      !lastRectRef.current
    ) {
      setExpandedIndex(null);
      return;
    }
    setOverlayShown(false);
    // Animate back to FIRST rect via property transitions
    const node = expandedCardRef.current;
    node.style.transition = `left ${FLIP_TRANSITION_DURATION} ease, top ${FLIP_TRANSITION_DURATION} ease, width ${FLIP_TRANSITION_DURATION} ease, height ${FLIP_TRANSITION_DURATION} ease`;
    node.style.left = `${firstRectRef.current.left}px`;
    node.style.top = `${firstRectRef.current.top}px`;
    node.style.width = `${firstRectRef.current.width}px`;
    node.style.height = `${firstRectRef.current.height}px`;
    const handleEnd = () => {
      setExpandedIndex(null);
      node.removeEventListener("transitionend", handleEnd);
      transitionEndNodeRef.current = null;
      transitionEndHandlerRef.current = null;
    };
    node.addEventListener("transitionend", handleEnd);
    transitionEndNodeRef.current = node;
    transitionEndHandlerRef.current = handleEnd;
  }, []);

  useEffect(() => {
    return () => {
      const node = transitionEndNodeRef.current;
      const handler = transitionEndHandlerRef.current;
      if (node && handler) {
        node.removeEventListener("transitionend", handler);
      }
      transitionEndNodeRef.current = null;
      transitionEndHandlerRef.current = null;
    };
  }, []);

  return {
    expandedIndex,
    overlayShown,
    expandedCardRef,
    firstRectRef,
    lastRectRef,
    handleExpand,
    handleClose,
  };
};
